import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  addCustomerSites,
  deleteCustomerSites,
  getAllCustomerSites,
  updateCustomerSites,
} from "controller/api";
import {
  ApiErrorType,
  GetAllCustomerSitesInput,
  UpdateCustomerSites,
} from "shared";
import { sentryLogger } from "utilz";

export const useGetAllCustomerSites = ({
  perPageCount,
  pageNumber,
  sortColumn,
  sortDirection,
  filter,
  searchQuery,
  customerId,
}: GetAllCustomerSitesInput) => {
  return useQuery({
    queryKey: [
      "sites",
      customerId,
      filter,
      sortColumn,
      sortDirection,
      perPageCount,
      pageNumber,
      searchQuery,
    ],
    queryFn: () =>
      getAllCustomerSites({
        perPageCount,
        pageNumber,
        sortColumn,
        sortDirection,
        filter,
        searchQuery,
        customerId,
      }),
    enabled: customerId ? true : false,
    onSuccess: (data) => {},
    onError: (error: ApiErrorType) => {
      sentryLogger({
        error: error,
        workflow: "customer",
        functionName: "getAllCustomerSites",
      });
    },
  });
};

export const useAddCustomerSites = () => {
  const queryClient = useQueryClient();
  return useMutation(
    ({ customerIdParams, data }: any) =>
      addCustomerSites({ customerIdParams, data }),
    {
      onSuccess: (data, variables, context) => {
        queryClient.invalidateQueries(["sites"]);
      },
      onError: (error: ApiErrorType, variables, context) => {
        sentryLogger({
          error: error,
          data: variables,
          workflow: "customer",
          level: "error",
          functionName: "addCustomerSites",
        });
      },
    }
  );
};

export const useUpdateCustomerSites = () => {
  const queryClient = useQueryClient();
  return useMutation(
    ({ siteId, customerId, data }: UpdateCustomerSites) =>
      updateCustomerSites({ siteId, customerId, data }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["sites"]);
      },
      onError: (error: ApiErrorType, variables, context) => {
        sentryLogger({
          error: error,
          workflow: "customer",
          level: "error",
          functionName: "updateCustomerSites",
        });
      },
    }
  );
};

export const useDeleteCustomerSites = () => {
  const queryClient = useQueryClient();
  return useMutation(
    ({
      customerIdParams,
      siteIds,
    }: {
      customerIdParams: string;
      siteIds: any;
    }) => deleteCustomerSites({ customerIdParams, siteIds }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["sites"]);
      },
      onError: (error: ApiErrorType, variables, context) => {
        sentryLogger({
          error: error,
          workflow: "customer",
          level: "error",
          functionName: "deleteCustomerSites",
        });
      },
    }
  );
};
